/** @jsx jsx */
/** @jsxRuntime classic */
import { Interpolation, jsx, Theme } from '@emotion/react';
import React, { useMemo } from 'react';
import useTheme from '../style/useTheme';
import { isInternetExplorer } from '../utils';

export type Props = {
  focused?: boolean;
  checked: boolean;
  onChange?: (checked: boolean) => void;
};

const BaseRadio: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  focused,
  checked,
  onChange,
}) => {
  const { theme, resolveColor } = useTheme();

  const handleClick = () => {
    if (onChange) {
      onChange(checked);
    }
  };

  const borderColor = useMemo(
    () =>
      resolveColor({
        type: 'component',
        value: theme.components.radio.default.iconBorder,
      }),
    [resolveColor, theme.components.radio]
  );

  const borderStyles: Interpolation<Theme> = useMemo(() => {
    if (isInternetExplorer())
      return {
        border: `${borderColor} 0.5px solid`,
        boxSizing: 'border-box',
        outline: 'none',
      };

    if (checked || focused) {
      return {
        border: `2px solid ${resolveColor({
          type: 'component',
          value: theme.components.radio.default.selected,
        })}`,
      };
    }

    return {
      border: `2px solid ${borderColor}`,
    };
  }, [
    borderColor,
    checked,
    focused,
    resolveColor,
    theme.components.radio.default.selected,
  ]);

  const radioDotColor = useMemo(
    () =>
      resolveColor({
        type: 'component',
        value: theme.components.radio.default.radioDotColor,
      }),
    [resolveColor, theme.components.radio]
  );

  return (
    <div
      css={{
        flexDirection: 'row',
        display: 'flex',
        alignItems: 'center',
      }}
      onClick={handleClick}
    >
      <div
        className="origami-Radio-outer-circle"
        css={{
          // outer circle
          minWidth: '22px',
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          marginRight: '9px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: checked
            ? resolveColor({
                type: 'component',
                value: theme.components.radio.default.selected,
              })
            : radioDotColor,
          ...borderStyles,
        }}
      >
        <div
          className="origami-Radio-inner-circle"
          css={{
            width: '8px',
            borderRadius: '50%',
            height: '8px',
            backgroundColor: radioDotColor,
          }}
        />
      </div>
      {children}
    </div>
  );
};

export default BaseRadio;
